import { useCallback, useEffect, useMemo, useState } from 'react'
import { addHook, removeAllHooks, sanitize } from 'dompurify'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { getMessage, setShowImages } from 'redux/features/mstore/mstoreSlice'
import { useMessageVariables } from 'components/libs/message/useMessageVariables'
import { useMessageLogRights } from 'components/libs/userRights/pages/useMessageLogRights'

const PREVIEW_IFRAME_ID = 'message-log-content'

export interface State {
  cantViewBlocked: boolean | undefined
  isShowImagesBannerVisible: boolean
  isPreviewDisabled: boolean | undefined
  previewDisabledReason: string | undefined
}

export interface EventHandlers {
  onClickShowImages: () => void
}

export type MessageDetailsContentTabMessagePreviewLogic = [State, EventHandlers]

export const useMessageDetailsContentTabMessagePreviewLogic = (): MessageDetailsContentTabMessagePreviewLogic => {
  const dispatch = useAppDispatch()
  const { message, showImages, userId } = useAppSelector(_store => ({
    message: _store.mstore.message,
    showImages: _store.mstore.showImages,
    userId: _store.auth.accessTokenObject?.userId
  }))
  const [iframeWindow, setIframeWindow] = useState<Window>()
  const [previewHtml, setPreviewHtml] = useState<string>()
  const {
    hasRightToSkipSendUserAction,
    isMessagePreviewAvailable,
    canViewBlockedMessages,
    canViewQuarantinedMessages
  } = useMessageLogRights()
  const { hasContent, hasVirus, hasImages, isLargeMessage, isAtpAffected, encrypted } = useMessageVariables()

  const isShowImagesBannerVisible = useMemo(() => !!(hasImages && !showImages), [hasImages, showImages])

  const previewDisabledReason = useMemo(() => {
    if (!message) {
      return undefined
    }

    switch (true) {
      case encrypted: {
        return 'encrypted'
      }
      case isAtpAffected: {
        return 'atd_detected'
      }
      case hasVirus: {
        return 'message_has_virus'
      }
      case isLargeMessage: {
        return 'message_too_large'
      }
      case !canViewBlockedMessages: {
        return 'cant_view_blocked'
      }
      case !canViewQuarantinedMessages: {
        return 'cant_view_quarantined'
      }
      case message.allowMsgBody === false: {
        return 'admin_disabled'
      }
      case !hasContent: {
        return 'no_content'
      }
      default:
        return ''
    }
  }, [
    canViewBlockedMessages,
    canViewQuarantinedMessages,
    hasVirus,
    isAtpAffected,
    isLargeMessage,
    message,
    hasContent,
    encrypted
  ])

  const onIframeMessage = useCallback((evt: MessageEvent) => {
    // BNESS-32455 — source validation: only accept messages whose source is the
    // outer preview iframe's own contentWindow. Anything else (other iframes,
    // extensions, Redux DevTools, dev-server HMR) is ignored. This closes the
    // attack where a malicious frame could trigger `preview-iframe-loaded` and
    // trick us into posting the admin's email body to it.
    const outerIframe = document.getElementById(PREVIEW_IFRAME_ID) as HTMLIFrameElement | null
    if (!outerIframe || evt.source !== outerIframe.contentWindow) {
      return
    }

    let data: any
    try {
      data = typeof evt.data === 'string' ? JSON.parse(evt.data) : evt.data
    } catch (e) {
      return
    }
    if (!data || typeof data !== 'object' || !data.type) {
      return
    }

    // Only one message type is meaningful to the parent today. `preview-content-changed`
    // is a height ping (CSS-driven, nothing to do here). `preview-link-click` is NOT
    // accepted — the previous auto-dispatch to handleQNDigestClickEvent allowed a
    // compromised email to synthesize privileged QN actions by posting a crafted
    // message. QN digest links now simply open in a new tab via native target=_blank,
    // landing the admin in an authenticated portal page where they can confirm.
    if (data.type === 'preview-iframe-loaded') {
      setIframeWindow(outerIframe.contentWindow as Window)
    }
  }, [])

  const onClickShowImages = useCallback(() => {
    if (!message) {
      return
    }
    dispatch(setShowImages(true))
    dispatch(
      getMessage({
        headersOnly: 0,
        messageId: message.mid,
        showImages: 1,
        domainId: message.did,
        userId: hasRightToSkipSendUserAction ? undefined : userId
      })
    )
  }, [dispatch, message, hasRightToSkipSendUserAction, userId])

  useEffect(() => {
    if (!message) {
      return
    }
    addHook('afterSanitizeAttributes', (node: any) => {
      // Set all elements owning target to target=_blank
      if ('target' in node) {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener')
      }
    })
    const html = sanitize(message.body, {
      FORCE_BODY: true,
      WHOLE_DOCUMENT: true,
      RETURN_DOM: true
    })
    html.style.margin = '0'
    html.style.padding = '0'
    const body = html.getElementsByTagName('body')[0]
    body.style.margin = '0'
    body.style.padding = '0'
    // BNESS-32455 — apply whiteSpace to the sanitized document body itself. Setting
    // whiteSpace on the <iframe> element in the parent does NOT style the nested
    // browsing context's document, so flowed-format wrapping would never render.
    const isFlowed = !!message.headers['Content-Type']
      ?.split(';')
      .find((s: string) => s.trim().toLowerCase() === 'format="flowed"')
    if (isFlowed) {
      body.style.whiteSpace = 'pre'
    }
    // BNESS-32455 — the inner iframe is sandboxed (opaque origin, no
    // allow-same-origin), so it cannot call functions on window.parent directly.
    // The injected script uses postMessage to report body height on load/resize.
    // Click handling (previously used for QN digest intercept) has been removed:
    // trusting messages from the preview realm to trigger privileged actions is a
    // forgery risk. QN digest links now open in a new tab via native target=_blank
    // and the admin confirms the action in the authenticated portal page.
    const script = document.createElement('script')
    script.innerHTML = `
(function(){
  var post = function(msg){ try { window.parent.parent.postMessage(JSON.stringify(msg), '*') } catch(e){} };
  var reportHeight = function(){
    var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight) + 20;
    post({ type: 'preview-content-changed', height: h });
  };
  window.addEventListener('load', reportHeight);
  window.addEventListener('resize', reportHeight);
})();
`.trim()
    body.appendChild(script)
    setPreviewHtml(html.outerHTML)
    removeAllHooks()
  }, [message])

  // Iframe posts a message when its body is ready
  // see iframe source in preview-loader.html.ts
  useEffect(() => {
    window.addEventListener('message', onIframeMessage, false)
    return () => window.removeEventListener('message', onIframeMessage)
  }, [onIframeMessage])

  // Set preview HTML when all dependencies are ready.
  // BNESS-32455 — we can no longer walk into the outer iframe's document to reach
  // the inner iframe (sandbox blocks same-origin DOM access). Send the sanitized
  // HTML to the outer loader via postMessage; the loader installs it into the
  // sandboxed inner iframe's srcdoc. Target the outer iframe's own window as the
  // origin — the loader additionally verifies that evt.source === window.parent.
  useEffect(() => {
    if (!iframeWindow || !previewHtml || !message) {
      return
    }
    iframeWindow.postMessage(JSON.stringify({ type: 'set-preview-html', html: previewHtml }), '*')
  }, [previewHtml, iframeWindow, message])

  return useMemo(
    () => [
      {
        cantViewBlocked: !canViewBlockedMessages,
        isShowImagesBannerVisible,
        isPreviewDisabled: !isMessagePreviewAvailable || encrypted,
        previewDisabledReason
      },
      {
        onClickShowImages
      }
    ],
    [
      canViewBlockedMessages,
      isMessagePreviewAvailable,
      isShowImagesBannerVisible,
      onClickShowImages,
      previewDisabledReason,
      encrypted
    ]
  )
}
